import type { RepPortalTabs } from "agent-portal";

import { TLPGrid, useGridColumns } from "@repo/baseCloud/components";
import { styles } from "@repo/helpers";
import { MoveableDialog } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import type { DisciplineRow, DisciplinesFilter } from "../../models";

import DisciplinesApi, { controller } from "../../api/disciplinesApi";
import { QUERY_KEYS } from "../../api/queries";
import { AgentPortal } from "../../apps/agentPortal";
import { getInitialDialogSize } from "../../helpers/agentPortal";
import style from "./style.module.scss";

interface Props {
	filters: DisciplinesFilter;
	setFilters: (filters: DisciplinesFilter) => void;
}

/** The Agent Portal record a row's button opens */
interface PortalRecord {
	repDisciplineId: number;
	repId: number;
	tab: RepPortalTabs;
	entityId: number;
}

const getGridColumns = () => DisciplinesApi.getGridColumns();
const exportDisciplines = (filters: DisciplinesFilter) => DisciplinesApi.getDisciplines(filters);

export function DisciplinesGrid({ filters, setFilters }: Props) {
	const [portalRecord, setPortalRecord] = useState<PortalRecord | null>(null);

	const disciplinesQuery = useQuery({
		queryKey: [QUERY_KEYS.Disciplines.Grid, filters],
		queryFn: ({ signal }) => DisciplinesApi.getDisciplines(filters, { signal }),
	});

	// Stable identity: useGridColumns rebuilds the columns, and the grid resets their state, when this changes
	const callbacks = useMemo(
		() => ({
			openPortalModal: (row: DisciplineRow) =>
				setPortalRecord({
					repDisciplineId: row.repDisciplineId,
					repId: row.representativeId,
					tab: "Discipline",
					entityId: row.repDisciplineId,
				}),
			// The Related Incident button passes only the incident id
			openIncident: (incidentId: number) => {
				const row = disciplinesQuery.data?.items.find((d) => d.relatedIncidentId === incidentId);
				if (!row) return;
				setPortalRecord({
					repDisciplineId: row.repDisciplineId,
					repId: row.representativeId,
					tab: row.relatedIncidentIsComplaint ? "Complaints" : "EnrollmentValidations",
					entityId: incidentId,
				});
			},
		}),
		[disciplinesQuery.data],
	);

	const { columns, isLoading: isLoadingColumns } = useGridColumns<DisciplineRow>(
		"Disciplines",
		getGridColumns,
		callbacks,
	);

	if (isLoadingColumns) return null;

	return (
		<>
			<TLPGrid<DisciplineRow, DisciplinesFilter>
				id="disciplinesGrid"
				title="Disciplines"
				gridNameForExport="Disciplines"
				keyProperty="repDisciplineId"
				filters={filters}
				setFilters={setFilters}
				query={disciplinesQuery}
				columns={columns}
				apiRoute={controller}
				gridApi={DisciplinesApi}
				exportFunc={exportDisciplines}
				selected={portalRecord ? [portalRecord.repDisciplineId] : []}
			/>
			{portalRecord ? (
				<MoveableDialog
					open
					onOpenChange={(open) => !open && setPortalRecord(null)}
					title="Agent Portal"
					initialSize={getInitialDialogSize()}
					resizable
					movable
					className={styles("agent-portal-modal", style.noPadding)}
					contentClassName={style.noPadding}
				>
					<AgentPortal
						key={`${portalRecord.tab}-${portalRecord.entityId}`}
						repId={portalRecord.repId}
						selectedTab={portalRecord.tab}
						entityId={portalRecord.entityId}
					/>
				</MoveableDialog>
			) : null}
		</>
	);
}
